import React from 'react';
import { FiEye, FiMousePointer, FiCopy, FiEdit2, FiTrash2, FiPause, FiPlay } from 'react-icons/fi';

const statusStyles = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  draft: 'bg-slate-100 text-slate-700',
  expired: 'bg-red-100 text-red-800'
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const AdTable = ({ ads, loading, onView, onEdit, onDelete, onToggleStatus, onCopyEmbed }) => {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        Loading advertisements…
      </div>
    );
  }

  if (!ads.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
        No advertisements yet. Create your first ad to get an embed code for publisher websites.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Ad</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Unique ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Schedule</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Weight</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Stats</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ads.map((ad) => (
              <tr key={ad._id} className="hover:bg-slate-50/70">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-20 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                      {ad.creativeType === 'video' ? (
                        <video src={ad.creative?.url} className="h-full w-full object-cover" muted />
                      ) : (
                        <img src={ad.creative?.url} alt={ad.title || ad.advertiserName} className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{ad.advertiserName}</p>
                      <p className="text-sm text-slate-500">{ad.title || 'Untitled ad'}</p>
                      <p className="text-xs uppercase text-slate-400">
                        {ad.creativeType} · {ad.displaySettings?.displayMode || 'popup'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">{ad.uniqueId}</code>
                </td>
                <td className="px-4 py-4 text-sm text-slate-600">
                  <div>{formatDate(ad.startDate)}</div>
                  <div className="text-slate-400">to {formatDate(ad.endDate)}</div>
                </td>
                <td className="px-4 py-4 text-sm font-medium text-slate-700">{ad.weight}</td>
                <td className="px-4 py-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <FiEye className="h-4 w-4 text-slate-400" />
                    {ad.impressions || 0}
                  </div>
                  <div className="mt-1 flex items-center gap-1">
                    <FiMousePointer className="h-4 w-4 text-slate-400" />
                    {ad.clicks || 0}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${statusStyles[ad.status] || statusStyles.draft}`}>
                    {ad.status}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button type="button" onClick={() => onCopyEmbed(ad)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Copy embed code">
                      <FiCopy className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => onView(ad)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="View details">
                      <FiEye className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => onEdit(ad)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Edit">
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    {ad.status !== 'expired' && (
                      <button type="button" onClick={() => onToggleStatus(ad)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title={ad.status === 'active' ? 'Pause' : 'Activate'}>
                        {ad.status === 'active' ? <FiPause className="h-4 w-4" /> : <FiPlay className="h-4 w-4" />}
                      </button>
                    )}
                    <button type="button" onClick={() => onDelete(ad)} className="rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-700" title="Delete">
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdTable;
